import { Dispatch, SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import { GHGEmissionsReportSettings } from '@/hooks'
import { Drawer, WidgetSpinner } from '@/components'
import { ResetReportSettingsButton } from '@/components/molecules/ResetReportSettingsButton/ResetReportSettingsButton'
import { DEFAULT_GHG_EMISSIONS_REPORT_SETTINGS } from '@/utils/constants'
import useSustainabilityStore from '../../stores/useSustainabilityStore/useSustainabilityStore'
import { EmissionScopeSelect } from '../TotalEmissionsDrawer/EmissionScopeSelect'
import { SettingsTextCard } from '@/components/molecules/SettingsTextCard/SettingsTextCard'
import { Scope2TypeSelect } from './Scope2TypeSelect'
import { TotalsViewToggle } from './TotalsViewToggle'

export type GHGEmissionsDrawerProps = {
    reportSettings: GHGEmissionsReportSettings
    setReportSettings: (settings: Partial<GHGEmissionsReportSettings>) => void
    isOpen: boolean
    setIsOpen: Dispatch<SetStateAction<boolean>>
    toggleButtonRef?: React.Ref<HTMLButtonElement>
    showResetReportSettingsButton?: boolean
    isMarketBasedScope2Available?: boolean
    isDataReady?: boolean
}

export const GHGEmissionsDrawer: React.FC<GHGEmissionsDrawerProps> = (
    props
) => {
    const {
        reportSettings,
        setReportSettings,
        isOpen,
        setIsOpen,
        toggleButtonRef,
        showResetReportSettingsButton,
        isDataReady,
        isMarketBasedScope2Available = true,
    } = props

    const { t } = useTranslation('translations')

    const hasDefaultSettings = useSustainabilityStore(
        (state) => state.ghgEmissions.hasDefaultSettings
    )
    const isResetHidden =
        showResetReportSettingsButton !== undefined
            ? !showResetReportSettingsButton
            : hasDefaultSettings

    return (
        <Drawer
            drawerToggleLabel={t('sustainability.report_settings.title')}
            drawerToggleIconName="tune"
            title={t('sustainability.report_settings.title')}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            toggleButtonRef={toggleButtonRef}
            headerContent={
                <ResetReportSettingsButton
                    onClick={() => {
                        setReportSettings(DEFAULT_GHG_EMISSIONS_REPORT_SETTINGS)
                    }}
                    isHidden={isResetHidden}
                    data-testid={'reset-settings-button'}
                />
            }
        >
            {isDataReady ? (
                <div className="space-y-7">
                    <div className="space-y-3">
                        <div className="text-heading-caps">
                            {t(
                                'sustainability.report_settings.operational_emissions'
                            )}
                        </div>
                        <EmissionScopeSelect
                            value={reportSettings.scope}
                            onChange={(scope) => {
                                setReportSettings({ scope })
                            }}
                        />
                        {reportSettings.scope !== 'scope2' && (
                            <SettingsTextCard
                                title={t(
                                    'sustainability.report_settings.scope1Type.title'
                                )}
                                description={t(
                                    'sustainability.report_settings.scope1Type.description'
                                )}
                            />
                        )}
                        {reportSettings.scope !== 'scope1' &&
                            reportSettings.scope2type && (
                                <Scope2TypeSelect
                                    isMarketBasedScope2Available={
                                        isMarketBasedScope2Available
                                    }
                                    value={reportSettings.scope2type}
                                    onChange={(scope2type) => {
                                        setReportSettings({
                                            scope2type,
                                        })
                                    }}
                                />
                            )}
                    </div>
                    <div className="space-y-3">
                        <div className="text-heading-caps">
                            {t('sustainability.report_settings.view_settings')}
                        </div>
                        <TotalsViewToggle
                            value={reportSettings.showTotals}
                            onChange={(showTotals) => {
                                setReportSettings({ showTotals })
                            }}
                        />
                    </div>
                </div>
            ) : (
                <div className="flex h-full items-center justify-center">
                    <WidgetSpinner />
                </div>
            )}
        </Drawer>
    )
}
